import type { StoreEnhancer } from '@reduxjs/toolkit';
import * as Sentry from '@sentry/react';
import { apiFailureOf, apiFailureTitle } from 'c-data-layer';
import { errorFamilyOf } from './sentryGrouping';

type SentryOptions = {
    dsn: string;
    environment?: string;
    /** Tests only: see every event before it would be sent; returning null drops it. */
    beforeSend?: Parameters<typeof Sentry.init>[0]['beforeSend'];
};

const pick = (source: Record<string, unknown> | undefined, keys: string[]) =>
    source == null
        ? undefined
        : Object.fromEntries(keys.filter(key => key in source).map(key => [key, source[key]]));

/**
 * The part of the Redux store sent with each error: request states and UI flags only. Entity
 * slices (campaigns, clients, users) and the logged-in user's record stay out; the user's id,
 * email and name already reach Sentry through `Sentry.setUser` (SentryInitialiser).
 */
export const sentryReduxState = (state: Record<string, any>) => ({
    ui: pick(state?.ui, [
        'drawerOpen',
        'metaTitle',
        'debugEnabled',
        'disableDesktopContentScrolling',
    ]),
    auth: pick(state?.auth, [
        'loginState',
        'validatingUserSession',
        'sessionTimedOut',
        'reloginState',
        'logoutState',
        'revalidateState',
        'startImpersonatingUser',
        'stopImpersonatingUser',
        'availableLoginTypes',
    ]),
    sessionCheck: state?.sessionCheck,
    passwordReset: state?.passwordReset,
});

/**
 * Turns a failed API request (an `ApiRequestError` from `useAPIClientRequest`, or a raw axios error
 * that escaped) into a readable event. Without it every one arrived as "Error: Error": the API often
 * sends no message, and the status and endpoint were dropped.
 * - Title: `POST /api/displays/screen/frame-search → 403 Forbidden: <API message>`.
 * - One issue per method + route + status (ids in paths become `:id`).
 * - 4xx is a warning; 5xx, timeouts and network failures stay errors.
 * - Requests our own code aborted are not reported.
 * - Context `api_request`: no bodies, only the API's message and field/parameter names.
 */
export const apiRequestFailureIntegration = (): Parameters<typeof Sentry.addIntegration>[0] => ({
    name: 'ApiRequestFailure',
    processEvent(event, hint) {
        const failure = apiFailureOf(hint.originalException);
        if (failure == null) return event;
        if (failure.cancelled) return null;

        const exception = event.exception?.values?.at(-1);
        if (exception) {
            exception.type = 'ApiRequestError';
            exception.value = apiFailureTitle(failure);
        }
        const isClientError =
            failure.status != null && failure.status >= 400 && failure.status < 500;

        return {
            ...event,
            level: isClientError ? 'warning' : 'error',
            fingerprint: ['api-request', failure.method, failure.route, failure.outcome],
            tags: {
                ...event.tags,
                'api.method': failure.method,
                'api.route': failure.route,
                'api.outcome': failure.outcome,
                ...(failure.code ? { 'api.code': failure.code } : {}),
            },
            contexts: {
                ...event.contexts,
                api_request: {
                    method: failure.method,
                    route: failure.route,
                    query_keys: failure.queryKeys,
                    status: failure.status,
                    status_text: failure.statusText,
                    code: failure.code,
                    api_message: failure.apiMessage,
                    validation_fields: failure.validationFields,
                },
            },
        };
    },
});

/** Groups the families in `sentryGrouping.ts` into one issue each, tagged by where they came from. */
export const errorGroupingIntegration = (): Parameters<typeof Sentry.addIntegration>[0] => ({
    name: 'ErrorGrouping',
    processEvent(event) {
        if (event.fingerprint != null) return event;
        const family = errorFamilyOf(event, window.location.origin);
        if (family == null) return event;

        const exception = event.exception?.values?.at(-1);
        if (exception && family.value) exception.value = family.value;

        return {
            ...event,
            fingerprint: family.fingerprint,
            level: family.level ?? event.level,
            tags: { ...event.tags, ...family.tags },
            contexts: { ...event.contexts, ...family.contexts },
        };
    },
});

/**
 * Start Sentry (exceptions only) and return the Redux enhancer that attaches `sentryReduxState` to every event.
 * `createStore` calls this only when VITE_SENTRY_DSN is set.
 */
export const initSentry = ({ dsn, environment, beforeSend }: SentryOptions): StoreEnhancer => {
    Sentry.init({
        dsn,
        environment,
        beforeSend,
        // Exceptions only. No browserTracingIntegration (performance tracing, which would also add
        // sentry-trace/baggage headers to API requests) and no tracesSampleRate. The default
        // browserSessionIntegration sends a session on every page load for release health; drop it.
        // The rest of the defaults are error handling: uncaught errors, breadcrumbs, dedupe.
        integrations: defaults => [
            ...defaults.filter(integration => integration.name !== 'BrowserSession'),
            apiRequestFailureIntegration(),
            errorGroupingIntegration(),
        ],
    });
    return Sentry.createReduxEnhancer({ stateTransformer: sentryReduxState });
};
